import React, { PureComponent } from 'react';
import {
    AirspaceRef,
    DynamicVolumeSetKindEnum,
    FlightAirspaceCrossing,
    FlightDto,
    FlightDynamicVolumeCrossing,
    FlightLevel,
    FlightLevelBand,
    FlightModelType,
    FlightPointDto,
    HotspotKind,
    IntegerRange,
    LevelAndTime,
    TimePeriod,
    VpdAirspaceOutlineDto,
    VpdDynamicVolumeOutlineDto,
    VpdFlightAirspaceCrossingProfileDto,
    VpdFlightDataDto,
} from 'app/model/CORE';
import { LevelScale, Range, TimeScale } from 'app/common/graph/Scale';
import TimeUtils from 'app/common/util/TimeUtils';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import Utils from 'app/common/util/Utils';
import { LevelAxis, TimeAxis, TimeLabelFormat } from 'app/common/graph/Axis';
import { AxisOrientation } from 'app/common/d3/GraphTypes';
import GraphUtils from 'app/common/graph/GraphUtils';
import AutoSizer from 'react-virtualized-auto-sizer';
import memoizeOne from 'memoize-one';
import FlightProfileUtils from 'app/common/util/FlightProfileUtils';
import FlightUtils from 'app/common/util/FlightUtils';
import { Tooltip } from 'antd';
import './VerticalProfile.less';
import TextBackgroundFilter from 'app/components/map/vpd/svg/TextBackgroundFilter';
import AppContext from 'app/common/context/AppContext';

import MeasureUtils from '../../../common/util/MeasureUtils';
import { getFillColour, ProfileSegmentHighlight } from '../model/MapModel';

/**
 * Equivalent to GWT AirspaceColourHighlight.
 * Note : I have not ported over the ability to specify an airspaceRef - this was not a good idea, given since R20
 * there is a strong possibility that a flight enters a given airspace several times.
 */
export interface AirspaceColourHighlight {
    airspace: AirspaceRef;
    entryTime?: Date;
    exitTime?: Date;
    trajectoryColour: string;
}

export interface VerticalProfileSvgProps {
    modelType: FlightModelType;
    flight: FlightDto;
    vpdCrossings: VpdFlightDataDto;
    highlightedPoints?: FlightPointDto[];
    highlightedSegments?: ProfileSegmentHighlight[];
    pointSelectionHandler?: (pt: FlightPointDto) => void;
    showIfpuzOnly: boolean;
    showDynamicVolumeCrossings: boolean;
    airspacesOfInterest: AirspaceColourHighlight[];
}

/**
 * A subset of property values that are key to defining the
 * Horizontal and vertical scale.
 * This values are needed in getDerivedStateFromProps in order to determine
 * if a scale re-computation is needed
 */
interface ScaleContext {
    modelType: FlightModelType;
    flight: FlightDto;
    showIfpuzOnly: boolean;
}
interface VerticalProfileSvgState {
    timeScale?: TimeScale;
    levelScale?: LevelScale;
    scaleContext?: ScaleContext;
}

const VERTICAL_AXIS_WIDTH = 40;
const HORZ_AXIS_HEIGHT = 20;
const MARGIN_RIGHT = 22;
const TEXT_BG_GREEN_FILTER_URL = 'textBgGreen';
const TEXT_BG_WHITE_FILTER_ID = 'textBgWhite';
const EXTRA_NUM_LEVELS_ABOVE_PROFILE = 70;
const DEFAULT_CHAR_WIDTH = 8;

interface VpdPoint {
    point?: FlightPointDto;
    time: Date;
    level: FlightLevel;
}

export default class VerticalProfileSvg extends PureComponent<VerticalProfileSvgProps, VerticalProfileSvgState> {
    //used for point selection onClick
    private flightPoints: Array<VpdPoint> = [];

    private airspaceCrossingsOfInterest = memoizeOne(
        (
            flight: FlightDto,
            modelType: FlightModelType,
            airspacesOfInterest: AirspaceColourHighlight[],
        ): FlightAirspaceCrossing[] => {
            return VerticalProfileSvg.getSelectedAirspaceCrossings(flight, modelType, airspacesOfInterest);
        },
    );

    constructor(props) {
        super(props);
        this.state = {
            timeScale: TimeScale.newTimeScale(
                new Range(0, 100),
                {
                    from: TimeUtils.parseHHMM('00:00'),
                    to: TimeUtils.parseHHMM('23:59'),
                },
                { from: TimeUtils.parseHHMM('00:00'), to: TimeUtils.parseHHMM('23:59') },
                20,
            ),
            levelScale: LevelScale.newLevelScale(
                new Range(100, 0),
                {
                    min: FlightLevelUtils.newFlightLevel(0),
                    max: FlightLevelUtils.newFlightLevel(999),
                },
                { min: FlightLevelUtils.newFlightLevel(0), max: FlightLevelUtils.newFlightLevel(999) },
                50,
            ),
        };
    }

    /**
     * Derive the new state based on the new props.
     * The main task is to determine if new logical scales are required.
     * Given this method is called before each render (even in cases where props have not changed), we need to ensure we only
     * compute the new scales if really required.
     * Therefore, we maintain in the state 'scaleContext' information that helps us decide
     * @param props
     * @param state
     */
    static getDerivedStateFromProps(
        props: VerticalProfileSvgProps,
        state: VerticalProfileSvgState,
    ): Partial<VerticalProfileSvgState> {
        if (
            props.flight !== state.scaleContext?.flight ||
            props.modelType !== state.scaleContext?.modelType ||
            props.showIfpuzOnly !== state.scaleContext?.showIfpuzOnly
        ) {
            const tp = VerticalProfileSvg.getTimePeriodOfInterest(props.flight, props.modelType, props.showIfpuzOnly);
            const fl = VerticalProfileSvg.getFlightLevelsOfInterest(
                props.flight,
                props.modelType,
                props.showIfpuzOnly,
                props.vpdCrossings,
            );
            //add some margin because of the labels
            const timePeriod: TimePeriod = {
                from: TimeUtils.addMinutes(tp.from, -10),
                to: TimeUtils.addMinutes(tp.to, 10),
            };

            const currTargetTimePeriod = state.timeScale.toTimePeriod(state.timeScale.getScale().target);
            const currTargetLevelBand = state.levelScale.toFlightLevelBand(state.levelScale.getScale().target);
            let newTargetTimePeriod = timePeriod;
            let newTargetLevelBand = fl;
            // determine if we can keep the current zoom settings in the case of a simple data refresh
            if (props.flight && state.scaleContext?.flight) {
                const eFid1 = FlightUtils.getEnrichedFlightId(props.flight);
                const eFid2 = FlightUtils.getEnrichedFlightId(state.scaleContext.flight);
                if (
                    FlightUtils.isSameEnrichedFlight(eFid1, eFid2) &&
                    props.modelType === state.scaleContext?.modelType &&
                    TimePeriodUtils.contains(timePeriod, currTargetTimePeriod) &&
                    FlightLevelUtils.contains(fl, currTargetLevelBand)
                ) {
                    newTargetLevelBand = currTargetLevelBand;
                    newTargetTimePeriod = currTargetTimePeriod;
                }
            }
            const newTimeScale = TimeScale.newTimeScale(
                state.timeScale.getScale().source,
                newTargetTimePeriod,
                timePeriod,
                20,
            );
            const newLevelScale = LevelScale.newLevelScale(
                state.levelScale.getScale().source,
                newTargetLevelBand,
                fl,
                50,
            );

            return {
                timeScale: newTimeScale,
                levelScale: newLevelScale,
                scaleContext: { flight: props.flight, modelType: props.modelType, showIfpuzOnly: props.showIfpuzOnly },
            };
        } else {
            return null; // must return value or null (never undefined)
        }
    }

    private static getTimePeriodOfInterest(flight: FlightDto, modelType: FlightModelType, ifpuzOnly: boolean) {
        if (Utils.isNotExists(flight) || Utils.isNotExists(modelType)) {
            return AppContext.getTodayPeriod();
        }
        const profile = FlightUtils.getPoints(flight, modelType);
        let timePeriod = null;
        if (ifpuzOnly) {
            const [entry, exit] = FlightProfileUtils.getIfpuzPeriod(FlightUtils.getAirspacePoints(flight, modelType));
            timePeriod = { from: entry.firstEntryTime, to: exit.lastExitTime };
        } else {
            timePeriod = { from: profile[0].timeOver, to: profile[profile.length - 1].timeOver };
        }
        return timePeriod;
    }

    /**
     * Return the FL band for this flight
     *  The algorithm has been simplified to default to [FL0 - FL (profile-max) + margin]
     *  We currently ignore the ifpuzOnly flag since it influences little the FL band.
     *  If 'other' airspace/reservations of interest are present, then we take into account their FL bands, which may be above the profile.
     * @param flight
     * @param modelType
     * @param ifpuzOnly
     * @param crossings
     */
    private static getFlightLevelsOfInterest(
        flight: FlightDto,
        modelType: FlightModelType,
        ifpuzOnly: boolean,
        crossings: VpdFlightDataDto,
    ): FlightLevelBand {
        if (Utils.isNotExists(flight) || Utils.isNotExists(modelType)) {
            return { min: FlightLevelUtils.newFlightLevel(0), max: FlightLevelUtils.newFlightLevel(999) };
        }
        const profile = FlightUtils.getPoints(flight, modelType);
        let minFL = 0,
            maxFL = 0;
        for (const fp of profile) {
            maxFL = Math.max(maxFL, FlightLevelUtils.getLevelValue(fp.flightLevel));
        }
        maxFL = maxFL + EXTRA_NUM_LEVELS_ABOVE_PROFILE;

        const cp = VerticalProfileSvg.getVpdCrossings(crossings, modelType);
        // ignore cp.airspaceCrossings (since covered by profile)
        cp?.airspaceReservationsOfInterest?.forEach(
            asp => (maxFL = Math.max(maxFL, VerticalProfileSvg.getMinMaxFlightLevels(asp.polygon).end)),
        );
        cp?.otherAirspaceOfInterest?.forEach(
            asp => (maxFL = Math.max(maxFL, VerticalProfileSvg.getMinMaxFlightLevels(asp.polygon).end)),
        );

        return { min: FlightLevelUtils.newFlightLevel(minFL), max: FlightLevelUtils.newFlightLevel(maxFL) };
    }

    private static getVpdCrossings(
        crossings: VpdFlightDataDto,
        modelType: FlightModelType,
    ): VpdFlightAirspaceCrossingProfileDto {
        if (Utils.isExists(crossings)) {
            switch (modelType) {
                case FlightModelType.CTFM:
                    return crossings.ctfmAirspaceCrossingProfile;
                case FlightModelType.RTFM:
                    return crossings.rtfmAirspaceCrossingProfile;
                case FlightModelType.FTFM:
                    return crossings.ftfmAirspaceCrossingProfile;
            }
        }
    }

    private static getMinMaxFlightLevels(array: Array<LevelAndTime>): IntegerRange {
        if (array) {
            let minFL = 999,
                maxFL = 0;
            for (const lat of array) {
                const level = FlightLevelUtils.getLevelValue(lat.level);
                if (level > maxFL) {
                    maxFL = level;
                }
                if (level < minFL) {
                    minFL = level;
                }
            }
            return { start: minFL, end: maxFL };
        }
    }

    getProfile(): FlightPointDto[] {
        return FlightUtils.getPoints(this.props.flight, this.props.modelType);
    }

    renderContent() {
        this.flightPoints = [];
        const selectedLabels = [];
        const trajectoriesPaths = [];
        const pointsCircles = [];
        const contentObjects = [];

        contentObjects.push(...this.renderPenetratedAirspace());

        //flight time period based on the map options
        const flightTimePeriod = VerticalProfileSvg.getTimePeriodOfInterest(
            this.props.flight,
            this.props.modelType,
            this.props.showIfpuzOnly,
        );

        if (this.props.flight) {
            const now = AppContext.getCurrentTime();
            const pastPoints = this.genPastPoints(now);
            let index = 0;
            if (pastPoints) {
                index = this.renderFlightTrajectory(
                    pastPoints,
                    now,
                    true,
                    flightTimePeriod,
                    trajectoriesPaths,
                    selectedLabels,
                    pointsCircles,
                    index,
                );
            }
            const futurePoints = this.genFuturePoints(now);
            if (futurePoints) {
                this.renderFlightTrajectory(
                    futurePoints,
                    now,
                    false,
                    flightTimePeriod,
                    trajectoriesPaths,
                    selectedLabels,
                    pointsCircles,
                    index,
                );
            }

            contentObjects.push(...trajectoriesPaths);
            contentObjects.push(...this.renderHighlightedSegments());
            contentObjects.push(...pointsCircles);
            contentObjects.push(...selectedLabels);
        }
        return contentObjects;
    }

    /*
     * This iterates over the flight points to generate the trajectory path. If there is airspace crossings of interest then the flight trajectory will be split into several paths. The blue paths and the "orange" paths.
     * The orange paths can start/end in points that are not part of the trajectory, in that case they'll be added as flight trajectory points so it can be used (only) in the computation of the orange paths.
     *
     * returns the number of generated points (circles)
     */
    renderFlightTrajectory(
        flightPoints: VpdPoint[],
        now: Date,
        isPast: boolean,
        flightTimePeriod: TimePeriod,
        trajectories: any[],
        selectedLabels: any[],
        points: any[],
        startIndex: number,
    ): number {
        let index = startIndex; //index that identifies the point position in the this.flightPoints (used in the click handler)
        let trajectoryIndex = 0; //index to be used in the trajectory paths - to make the key unique
        if (flightPoints) {
            const key = isPast ? 'past' : 'future';

            //create a array with all point times
            const pointsTimes = flightPoints.map(vpd => vpd.time.getTime());

            //if there is airspace crossings of interest add their times to the flightPoints but only if there isn't already a "real" point at that time
            if (this.getAirspaceCrossingsOfInterest()) {
                for (const flightAirspaceCrossing of this.getAirspaceCrossingsOfInterest()) {
                    const airspaceCrossingTimePeriod = {
                        from: flightAirspaceCrossing.firstEntryTime,
                        to: flightAirspaceCrossing.lastExitTime,
                    };
                    if (TimePeriodUtils.intersects(flightTimePeriod, airspaceCrossingTimePeriod)) {
                        if (isPast) {
                            if (
                                flightAirspaceCrossing.firstEntryTime <= now &&
                                TimePeriodUtils.containsInc(flightTimePeriod, flightAirspaceCrossing.firstEntryTime) &&
                                !pointsTimes.includes(flightAirspaceCrossing.firstEntryTime.getTime())
                            ) {
                                flightPoints.push({
                                    time: flightAirspaceCrossing.firstEntryTime,
                                    level: flightAirspaceCrossing.firstEntryFlightLevel,
                                });
                            }
                            if (
                                flightAirspaceCrossing.lastExitTime <= now &&
                                TimePeriodUtils.containsInc(flightTimePeriod, flightAirspaceCrossing.lastExitTime) &&
                                !pointsTimes.includes(flightAirspaceCrossing.lastExitTime.getTime())
                            ) {
                                flightPoints.push({
                                    time: flightAirspaceCrossing.lastExitTime,
                                    level: flightAirspaceCrossing.lastExitFlightLevel,
                                });
                            }
                        } else {
                            if (
                                flightAirspaceCrossing.firstEntryTime >= now &&
                                TimePeriodUtils.containsInc(flightTimePeriod, flightAirspaceCrossing.firstEntryTime) &&
                                !pointsTimes.includes(flightAirspaceCrossing.firstEntryTime.getTime())
                            ) {
                                flightPoints.push({
                                    time: flightAirspaceCrossing.firstEntryTime,
                                    level: flightAirspaceCrossing.firstEntryFlightLevel,
                                });
                            }
                            if (
                                flightAirspaceCrossing.lastExitTime >= now &&
                                TimePeriodUtils.containsInc(flightTimePeriod, flightAirspaceCrossing.lastExitTime) &&
                                !pointsTimes.includes(flightAirspaceCrossing.lastExitTime.getTime())
                            ) {
                                flightPoints.push({
                                    time: flightAirspaceCrossing.lastExitTime,
                                    level: flightAirspaceCrossing.lastExitFlightLevel,
                                });
                            }
                        }
                    }
                }
                //keep the points ordered
                flightPoints = flightPoints.sort((p1, p2) => TimeUtils.compare(p1.time, p2.time));
            }

            let trajectoryPoints: Array<VpdPoint> = []; //blue line points
            let airspaceOfInterestPoints: Array<VpdPoint> = []; //airspace of interest ('orange') points
            let airspaceOfInterestColor = undefined; //airspace of interest colour
            let previousPoint = undefined;

            for (const fPoint of flightPoints) {
                this.flightPoints.push(fPoint);
                const pointInAirspaceOfInterest = this.isPointInAirspaceOfInterest(fPoint);

                //if current point within airspace of interest
                if (pointInAirspaceOfInterest) {
                    //saves the airspace of interest colour if is just entering
                    if (!airspaceOfInterestColor)
                        airspaceOfInterestColor = this.getAirspaceColour(this.getAirspaceOfInterestWhereIs(fPoint));

                    //save the point in the airspace of interest points
                    airspaceOfInterestPoints.push(fPoint);

                    if (trajectoryPoints.length > 0) {
                        //if entering in airspace of interest and there were points before it then add them as a path (blue line)
                        trajectoryPoints.push(fPoint);
                        const trajectoryPointsPath = this.genPath(trajectoryPoints);
                        trajectories.push(
                            <path
                                fill="none"
                                key={`flight-trajectory-${key}-${trajectoryIndex++}`}
                                d={trajectoryPointsPath}
                                stroke="#60B1CC"
                                strokeWidth={2}
                                strokeDasharray={isPast ? '4 2' : undefined}
                            />,
                        );
                        //reset trajectory points because it was just added as a path
                        trajectoryPoints = [];
                    }
                } else {
                    //if there are airspace of interest points before the current point then add them as a airspace of interest path
                    if (airspaceOfInterestPoints.length > 0) {
                        const airspacePath = this.genPath(airspaceOfInterestPoints);
                        trajectories.push(
                            <path
                                fill="none"
                                key={`flight-crossing-${key}-${trajectoryIndex++}`}
                                d={airspacePath}
                                stroke={airspaceOfInterestColor}
                                strokeWidth={2.5}
                                strokeDasharray={isPast ? '4 2' : undefined}
                            />,
                        );
                        //reset
                        airspaceOfInterestPoints = [];
                        airspaceOfInterestColor = null;

                        //the previous point will be the following path starting point, as this point is not selected but the previous was and so the non selected trajectory starts where the selected trajectory ends
                        if (previousPoint) {
                            trajectoryPoints.push(previousPoint);
                        }
                    }

                    trajectoryPoints.push(fPoint);
                }

                const pointSelected = this.isPointSelected(fPoint.point);
                if (fPoint.point && !FlightProfileUtils.isVectorPoint(fPoint.point)) {
                    const pointColour = pointSelected ? '#FFFF99' : this.getPointColor(fPoint, '#FFFF9940');
                    const pointStrokeColour = this.getPointColor(fPoint, '#FFFF99');
                    points.push(
                        <Tooltip
                            key={`${key}Point-tooltip-${fPoint.time.getTime()}-${this.props.modelType}-${index}`}
                            title={this.getLabel(fPoint)}>
                            <circle
                                className="point"
                                id={index.toString()}
                                cx={this.state.timeScale.toSource(fPoint.time)}
                                cy={this.state.levelScale.toSource(fPoint.level)}
                                r={3.5}
                                stroke={pointStrokeColour}
                                strokeWidth={1}
                                fill={pointColour}
                                key={`${key}Point-${fPoint.time.getTime()}-${this.props.modelType}-${index}`}
                                onClick={this.handleClickPoint}
                            />
                        </Tooltip>,
                    );
                }
                if (pointSelected) {
                    selectedLabels.push(
                        <text
                            key={`${key}Point-label${fPoint.time.getTime()}`}
                            filter={TEXT_BG_GREEN_FILTER_URL}
                            x={this.state.timeScale.toSource(fPoint.time)}
                            y={this.state.levelScale.toSource(fPoint.level)}
                            dx={5}
                            fill="black"
                            fontWeight="bold">
                            {this.getLabel(fPoint)}
                        </text>,
                    );
                }
                index++;
                previousPoint = fPoint;
            }
            //add remaining points as paths
            if (trajectoryPoints.length > 0) {
                const trajectoryPointsPath = this.genPath(trajectoryPoints);
                trajectories.push(
                    <path
                        fill="none"
                        key={`flight-trajectory-${key}-${trajectoryIndex++}`}
                        d={trajectoryPointsPath}
                        stroke="#60B1CC"
                        strokeWidth={2}
                        strokeDasharray={isPast ? '4 2' : undefined}
                    />,
                );
            }
            if (airspaceOfInterestPoints.length > 0) {
                const airspacePath = this.genPath(airspaceOfInterestPoints);
                trajectories.push(
                    <path
                        fill="none"
                        key={`flight-crossing-${key}-${trajectoryIndex++}`}
                        d={airspacePath}
                        stroke={airspaceOfInterestColor}
                        strokeWidth={2.5}
                        strokeDasharray={isPast ? '4 2' : undefined}
                    />,
                );
            }
        }
        return index;
    }

    renderPenetratedAirspace() {
        const contentObjects = [];
        const airspaceCrossingProfile = this.getVpdAirpsaceCrossingProfile();
        if (airspaceCrossingProfile) {
            if (airspaceCrossingProfile.airspaceCrossings) {
                contentObjects.push(
                    ...airspaceCrossingProfile.airspaceCrossings.map((outline, index) =>
                        this.drawPolygonOutline(
                            outline.polygon,
                            '#191919',
                            this.getAirspaceOutlineBackgroundColour(outline),
                            `aspCr-${outline.airspaceId}-${index}`,
                        ),
                    ),
                );
            }
            if (airspaceCrossingProfile.otherAirspaceOfInterest) {
                contentObjects.push(
                    ...airspaceCrossingProfile.otherAirspaceOfInterest.map((outline, index) =>
                        this.drawPolygonOutline(
                            outline.polygon,
                            'white',
                            this.getAirspaceOutlineBackgroundColour(outline),
                            `aspInt-${outline.airspaceId}-${index}`,
                        ),
                    ),
                );
            }
            if (airspaceCrossingProfile.airspaceReservationsOfInterest && this.props.showDynamicVolumeCrossings) {
                contentObjects.push(
                    ...airspaceCrossingProfile.airspaceReservationsOfInterest.map((outline, index) =>
                        this.drawPolygonOutline(
                            outline.polygon,
                            'white',
                            this.getDynamicVolumeOutlineColour(outline),
                            `aspRes-${outline.dynamicVolumeId}-${index}`,
                        ),
                    ),
                );
            }
            if (airspaceCrossingProfile.airspaceCrossings) {
                contentObjects.push(
                    ...airspaceCrossingProfile.airspaceCrossings.map((outline, index) =>
                        this.drawLabel(outline.centroid, outline.airspaceId, 'ac' + index),
                    ),
                );
            }
            if (airspaceCrossingProfile.otherAirspaceOfInterest) {
                contentObjects.push(
                    ...airspaceCrossingProfile.otherAirspaceOfInterest.map((outline, index) =>
                        this.drawLabel(outline.centroid, outline.airspaceId, 'oai' + index),
                    ),
                );
            }
            if (airspaceCrossingProfile.airspaceReservationsOfInterest && this.props.showDynamicVolumeCrossings) {
                contentObjects.push(
                    ...airspaceCrossingProfile.airspaceReservationsOfInterest.map((outline, index) =>
                        this.drawLabel(outline.centroid, outline.dynamicVolumeId, 'dv' + index),
                    ),
                );
            }
        }
        return contentObjects;
    }

    renderHighlightedSegments() {
        return this.props.highlightedSegments
            ? this.props.highlightedSegments.map(segment => this.drawHighlightedSegment(segment))
            : [];
    }

    drawHighlightedSegment(segment: ProfileSegmentHighlight) {
        const points = this.genPointsForPeriod(segment.timePeriod);

        if (Utils.isEmpty(points)) return;

        const firstPoint = this.genCoordinate(points[0]);
        let path = String('M ' + firstPoint[0] + ' ' + firstPoint[1] + ' L ');
        for (let idx = 1; idx < points.length; idx++) {
            const coordinate = this.genCoordinate({ time: points[idx].time, level: points[idx].level });
            path = path.concat(coordinate[0] + ' ' + coordinate[1] + ' ');
        }
        return (
            <path
                key={TimeUtils.formatDDHHMM(segment.timePeriod.from) + TimeUtils.formatDDHHMM(segment.timePeriod.to)}
                d={path.trim().toString()}
                stroke={getFillColour(segment.loadClassification)}
                strokeWidth={4}
                strokeOpacity={1}
                fill="none"
            />
        );
    }

    drawPolygonOutline(polygon: Array<LevelAndTime>, strokeStyle: string, fillStyle: string, key: string) {
        if (Utils.isEmpty(polygon)) return;
        const firstPoint = this.genCoordinate({ time: polygon[0].time, level: polygon[0].level });
        let path = String('M ' + firstPoint[0] + ' ' + firstPoint[1] + ' L ');
        for (let idx = 1; idx < polygon.length; idx++) {
            const coordinate = this.genCoordinate({ time: polygon[idx].time, level: polygon[idx].level });
            path = path.concat(coordinate[0] + ' ' + coordinate[1] + ' ');
        }
        return <path key={key} d={path.trim().toString()} stroke={strokeStyle} fill={fillStyle} fillOpacity={'40%'} />;
    }

    drawLabel(centroid: LevelAndTime, label: string, keySuffix?: string | number) {
        if (!centroid) return;
        const coordinate = this.genCoordinate({ time: centroid.time, level: centroid.level });
        return (
            <text
                key={`label-${label}-${centroid.time}-${keySuffix}`}
                x={coordinate[0] - this.getTextWidth(label) / 2}
                y={coordinate[1]}
                dx={5}
                fill={'#A0A0A0'}>
                {label}
            </text>
        );
    }

    private getAirspaceOutlineBackgroundColour(outline: VpdAirspaceOutlineDto): string {
        if (Utils.isNotEmpty(outline.regulationIds)) {
            // close to "cyan" (regulation colour) but easier on the eyes;
            return outline.hasMpr ? 'darkturquoise' : 'url(#vpd-regulation-hatched)';
        }

        if (Utils.isNotEmpty(outline.hotspots)) {
            const hs = MeasureUtils.getHighestHotspot(outline.hotspots);
            if (hs) {
                switch (hs.kind) {
                    case HotspotKind.LOCATION_OF_INTEREST:
                    case HotspotKind.PROBLEM:
                        return '#edbbbb';
                    case HotspotKind.PROTECTION:
                        return 'url(#vpd-hotspot-hatched)';
                }
            }
            return '#edbbbb';
        }
        return '#3A3A3A';
    }

    private getDynamicVolumeOutlineColour(outline: VpdDynamicVolumeOutlineDto): string {
        if (outline.active) {
            return outline.kind === DynamicVolumeSetKindEnum.ARES_RESERVATION ? '#e50b0b' : '#2124ed';
        } else {
            return outline.kind === DynamicVolumeSetKindEnum.ARES_RESERVATION ? '#e59797' : '#9394e2';
        }
    }

    /**
     * Return the ASP Crossing profile for the required flight model.
     */
    getVpdAirpsaceCrossingProfile(): VpdFlightAirspaceCrossingProfileDto {
        if (this.props.flight && this.props.modelType && this.props.vpdCrossings) {
            switch (this.props.modelType) {
                case FlightModelType.CTFM:
                    return this.props.vpdCrossings.ctfmAirspaceCrossingProfile;
                case FlightModelType.RTFM:
                    return this.props.vpdCrossings.rtfmAirspaceCrossingProfile;
                case FlightModelType.FTFM:
                    return this.props.vpdCrossings.ftfmAirspaceCrossingProfile;
            }
        }
    }

    isPointSelected(pt: FlightPointDto): boolean {
        if (pt && this.props.highlightedPoints?.length > 0) {
            return this.props.highlightedPoints.includes(pt);
        }
        return false;
    }

    isPointInAirspaceOfInterest(point: VpdPoint): boolean {
        return this.getAirspaceCrossingsOfInterest()
            ? this.getAirspaceCrossingsOfInterest().filter(
                  segment => segment.firstEntryTime <= point.time && segment.lastExitTime >= point.time,
              ).length > 0
            : false;
    }

    getLabel(pt: VpdPoint) {
        if (pt && pt.point) {
            let str = FlightProfileUtils.getDisplayString(pt.point);
            if (str)
                str =
                    str +
                    (' (' +
                        TimeUtils.formatHHMM(pt.time) +
                        '@' +
                        FlightLevelUtils.toString(pt.point.flightLevel) +
                        ')');
            return str ? str : TimeUtils.formatHHMM(pt.time) + '/' + FlightLevelUtils.toString(pt.point.flightLevel);
        }
        return null;
    }

    //for the moment I would take a simplistic approach where I've defined a constant value to be multiplied by the number of chars of a given text
    getTextWidth(text: string): number {
        return text.trim().length * DEFAULT_CHAR_WIDTH;
    }

    genPath(points: Array<VpdPoint>): string {
        if (points && points.length > 0) {
            //first point is a move
            const firstPointCoordinates = this.genCoordinate(points[0]);
            if (Utils.isNotExists(firstPointCoordinates)) {
                console.error('Invalid Vpd Point:', points[0]);
                return undefined;
            }
            let path: string = 'M ' + firstPointCoordinates[0] + ' ' + firstPointCoordinates[1] + ' L ';

            for (let idx = 1; idx < points.length; idx++) {
                const pointCoordinates = this.genCoordinate(points[idx]);
                if (Utils.isNotExists(pointCoordinates)) {
                    console.error('Invalid Vpd Point', points[idx]);
                    return undefined;
                }
                path = path.concat(pointCoordinates[0] + ' ' + pointCoordinates[1] + ' ');
            }
            return path.toString();
        }
        return undefined;
    }

    getAirspaceCrossingsOfInterest(): FlightAirspaceCrossing[] {
        // memoized value
        return this.airspaceCrossingsOfInterest(
            this.props.flight,
            this.props.modelType,
            this.props.airspacesOfInterest,
        );
    }

    getAirspaceOfInterestWhereIs(point: VpdPoint): FlightAirspaceCrossing {
        if (Utils.isEmpty(this.props.airspacesOfInterest)) return undefined;

        for (const selectedAirspaceCrossing of this.getAirspaceCrossingsOfInterest()) {
            const timePeriod: TimePeriod = {
                from: selectedAirspaceCrossing.firstEntryTime,
                to: selectedAirspaceCrossing.lastExitTime,
            };
            if (TimePeriodUtils.containsInc(timePeriod, point.time)) {
                return selectedAirspaceCrossing;
            }
        }
        return undefined;
    }

    //use memoize (props as parameters)
    getPointColor(point: VpdPoint, defaultColour: string): string {
        const ac = this.getAirspaceOfInterestWhereIs(point);
        if (Utils.isExists(ac)) {
            return this.getAirspaceColour(ac);
        }
        return defaultColour;
    }

    getAirspaceColour(airspace: FlightAirspaceCrossing): string {
        if (Utils.isEmpty(this.props.airspacesOfInterest)) return undefined;

        for (const airspaceColourHighlight of this.props.airspacesOfInterest) {
            if (airspaceColourHighlight.airspace.id === airspace.airspaceId) {
                return airspaceColourHighlight.trajectoryColour;
            }
        }
        return undefined;
    }

    private genCoordinate(point: VpdPoint): [number, number] {
        if (point && point.time && point.level) {
            return [this.state.timeScale.toSource(point.time), this.state.levelScale.toSource(point.level)];
        }
        return null;
    }

    genPastPoints = (now: Date): VpdPoint[] => {
        const tp = VerticalProfileSvg.getTimePeriodOfInterest(
            this.props.flight,
            this.props.modelType,
            this.props.showIfpuzOnly,
        );
        return tp.from.getTime() >= now.getTime()
            ? null
            : this.genPointsForPeriod({
                  from: tp.from,
                  to: TimeUtils.min(now, tp.to),
              });
    };

    genPastPointsForCrossings(now: Date): Map<string, VpdPoint[][]> {
        if (Utils.isEmpty(this.props.airspacesOfInterest)) return undefined;
        const tp = VerticalProfileSvg.getTimePeriodOfInterest(
            this.props.flight,
            this.props.modelType,
            this.props.showIfpuzOnly,
        );
        if (tp.from.getTime() >= now.getTime()) return undefined;
        const out = new Map<string, VpdPoint[][]>();
        for (const selectedAirspaceCrossing of this.getAirspaceCrossingsOfInterest()) {
            const intersectionPeriod = TimePeriodUtils.intersection(tp, {
                from: selectedAirspaceCrossing.firstEntryTime,
                to: selectedAirspaceCrossing.lastExitTime,
            });
            if (Utils.isExists(intersectionPeriod)) {
                const vpdPoints = this.genPointsForPeriod(intersectionPeriod);
                const airspaceColour = this.getAirspaceColour(selectedAirspaceCrossing);
                if (Utils.isNotExists(out.get(airspaceColour))) {
                    out.set(airspaceColour, []);
                }
                out.get(airspaceColour).push(vpdPoints);
            }
        }
        return out.size > 0 ? out : undefined;
    }

    genFuturePointsForCrossings(now: Date): Map<string, VpdPoint[][]> {
        if (Utils.isEmpty(this.props.airspacesOfInterest)) return undefined;
        const tp = VerticalProfileSvg.getTimePeriodOfInterest(
            this.props.flight,
            this.props.modelType,
            this.props.showIfpuzOnly,
        );
        if (tp.to.getTime() <= now.getTime()) return undefined;
        const out = new Map<string, VpdPoint[][]>();
        for (const selectedAirspaceCrossing of this.getAirspaceCrossingsOfInterest()) {
            const intersectionPeriod = TimePeriodUtils.intersection(tp, {
                from: selectedAirspaceCrossing.firstEntryTime,
                to: selectedAirspaceCrossing.lastExitTime,
            });
            if (Utils.isExists(intersectionPeriod)) {
                const vpdPoints = this.genPointsForPeriod(intersectionPeriod);
                const airspaceColour = this.getAirspaceColour(selectedAirspaceCrossing);
                if (Utils.isNotExists(out.get(airspaceColour))) {
                    out.set(airspaceColour, []);
                }
                out.get(airspaceColour).push(vpdPoints);
            }
        }
        return out.size > 0 ? out : undefined;
    }

    genFuturePoints = (now: Date): VpdPoint[] => {
        const tp = VerticalProfileSvg.getTimePeriodOfInterest(
            this.props.flight,
            this.props.modelType,
            this.props.showIfpuzOnly,
        );
        return tp.to.getTime() <= now.getTime() ? null : this.genPointsForPeriod({ from: now, to: tp.to });
    };

    /**
     * Return a subset of ChartPoints that represent a [from, to[ segment of the profile.
     * If need be, then interpolated points are added at the from/to times.
     */
    genPointsForPeriod = (tp: TimePeriod): VpdPoint[] => {
        const reply: Array<VpdPoint> = [];
        const profile = this.getProfile();
        const startIdx = FlightProfileUtils.getPointIdxAfterOrAt(profile, tp.from);
        const endIdx = FlightProfileUtils.getPointIdxBeforeOrAt(profile, tp.to);

        if (startIdx < 0) return []; // the time period couldn't be found in the current profile

        if (profile[startIdx].timeOver.getTime() > tp.from.getTime() && startIdx > 0) {
            reply.push({
                time: tp.from,
                level: FlightProfileUtils.interpolateLevelAtTime(profile, tp.from, startIdx - 1),
            });
            // startIdx++;
        }
        for (let i = startIdx; i <= endIdx; i++) {
            reply.push({
                time: profile[i].timeOver,
                level: profile[i].flightLevel,
                point: profile[i],
            });
        }
        if (profile[endIdx].timeOver.getTime() < tp.to.getTime() && profile.length > endIdx + 1) {
            reply.push({
                time: tp.to,
                level: FlightProfileUtils.interpolateLevelAtTime(profile, tp.to, endIdx),
            });
        }
        return reply;
    };

    renderBackground(width: number, height: number) {
        return (
            <rect
                x={VERTICAL_AXIS_WIDTH}
                y={0}
                fill="#191919"
                width={width - VERTICAL_AXIS_WIDTH - MARGIN_RIGHT}
                height={height - HORZ_AXIS_HEIGHT}
            />
        );
    }

    /**
     * Rendering a horizontal grid with the same intervals as the LevelAxis
     * @param interval
     */
    renderHorizontalGrid(interval, color: string) {
        const tscale = this.state.timeScale.getScale();
        const lscale = this.state.levelScale.getScale();
        const range = lscale.target;
        let value = range.getFirstValue(interval);
        const increment = range.from <= range.to ? interval : -interval;
        let path = '';
        while (range.contains(value)) {
            const sourceVal = lscale.toSource(value);
            if (lscale.source.contains(sourceVal)) {
                path += ' M' + tscale.source.from + ',' + sourceVal + ' L' + tscale.source.to + ',' + sourceVal;
            }
            value += increment;
        }
        return <path d={path} stroke={color} shapeRendering="geometricPrecision" />;
    }

    /**
     * Rendering a vertical grid with the same intervals as the TimeAxis
     * @param interval
     */
    renderVerticalGrid(interval) {
        const tscale = this.state.timeScale.getScale();
        const lscale = this.state.levelScale.getScale();
        const range = tscale.target;
        let value = range.getFirstValue(interval);
        const increment = range.from <= range.to ? interval : -interval;
        let path = '';
        while (range.contains(value)) {
            const sourceVal = tscale.toSource(value);
            if (tscale.source.contains(sourceVal)) {
                path += ' M' + sourceVal + ',' + lscale.source.from + ' L' + sourceVal + ',' + lscale.source.to;
            }
            value += increment;
        }
        return <path d={path} stroke="lightgrey" />;
    }

    render() {
        return (
            <AutoSizer onResize={this.onResize}>
                {({ height, width }) => {
                    // we ensure width/height are taken into account by calling directly scale.setSource.
                    // we do *not* call setState here since its not recommended from within a render call - hence the onResize method
                    //
                    // for components that depend only on scale (and not directly on width/height), following a resize, in order to ensure they re-render correctly, we have 2 options
                    // a) call setState(timeScale, levelScale) -> the onResize method demonstrates this. Its simple .. but does cause 2 renders
                    // b) implement dedicated shouldComponentUpdate methods on components. More efficient but messy
                    this.state.timeScale.setSource(new Range(VERTICAL_AXIS_WIDTH, width - MARGIN_RIGHT));
                    this.state.levelScale.setSource(new Range(height - HORZ_AXIS_HEIGHT, 0));

                    const bestTimeInterval = TimeAxis.chooseTickInterval(
                        this.state.timeScale,
                        7,
                        TimeLabelFormat.HH_MM,
                        AxisOrientation.BOTTOM,
                    );
                    const bestLevelInterval = LevelAxis.chooseTickInterval(
                        this.state.levelScale,
                        11,
                        AxisOrientation.LEFT,
                    );
                    const clip1Id = GraphUtils.getUniqueClipId();
                    return (
                        <div style={{ height: height, width: width }}>
                            <svg width="100%" height="100%">
                                <defs>
                                    <clipPath id={clip1Id}>
                                        <rect
                                            x={VERTICAL_AXIS_WIDTH}
                                            y={0}
                                            width={width - VERTICAL_AXIS_WIDTH - MARGIN_RIGHT}
                                            height={height - HORZ_AXIS_HEIGHT}
                                        />
                                    </clipPath>
                                    <TextBackgroundFilter id={TEXT_BG_GREEN_FILTER_URL} color="lightgreen" />
                                    <TextBackgroundFilter id={TEXT_BG_WHITE_FILTER_ID} color="white" />
                                </defs>
                                <LevelAxis
                                    tickInterval={bestLevelInterval}
                                    orientation={AxisOrientation.LEFT}
                                    levelScale={this.state.levelScale}
                                    width={VERTICAL_AXIS_WIDTH}
                                    height={height - HORZ_AXIS_HEIGHT}
                                    y={0}
                                    x={0}
                                />

                                <TimeAxis
                                    tickInterval={bestTimeInterval}
                                    orientation={AxisOrientation.BOTTOM}
                                    labelFormat={TimeLabelFormat.HH_MM}
                                    timeScale={this.state.timeScale}
                                    width={width - VERTICAL_AXIS_WIDTH - MARGIN_RIGHT}
                                    height={HORZ_AXIS_HEIGHT}
                                    x={VERTICAL_AXIS_WIDTH}
                                    y={height - HORZ_AXIS_HEIGHT}
                                />
                                {/*<ScaleBar direction={Direction.VERTICAL} scale={this.state.levelScale.getScale()}
                                          width={SCALE_BAR_THICKNESS} height={height - HORZ_AXIS_HEIGHT} x={8} y={0}
                                          onRescale={this.onLevelRescale}/>
                                <ScaleBar direction={Direction.HORIZONTAL} scale={this.state.timeScale.getScale()}
                                          width={width - VERTICAL_AXIS_WIDTH - MARGIN_RIGHT}
                                          height={SCALE_BAR_THICKNESS} x={VERTICAL_AXIS_WIDTH} y={height - 21}
                                          onRescale={this.onTimeRescale}/>*/}

                                <g clipPath={`url(#${clip1Id})`}>
                                    {this.renderBackground(width, height)}
                                    {this.renderHorizontalGrid(10 * FlightLevelUtils.FEET_PER_FLIGHT_LEVEL, '#202020')}
                                    {this.renderHorizontalGrid(bestLevelInterval, '#404040')}
                                    {/*this.renderVerticalGrid(bestTimeInterval)*/}
                                    {this.renderContent()}
                                </g>
                            </svg>
                        </div>
                    );
                }}
            </AutoSizer>
        );
    }

    // HANDLERS

    onTimeRescale = (target: Range) => {
        this.setState(state => {
            return { timeScale: TimeScale.forNewTarget(state.timeScale, target) };
        });
    };
    onLevelRescale = (target: Range) => {
        this.setState(state => {
            return { levelScale: LevelScale.forNewTarget(state.levelScale, target) };
        });
    };

    /**
     * The AutoSizer has a weakness - it gives you no means to say
     * "Just tell me when you resize, and I'll change my own state, and therefore control when render is done"
     * Instead Autosizer *ALWAYS* call render ..
     * So when you need to update state dependent upon width/height, you end up rendering TWICE.
     * This is just a weakness we will have to live with for the moment..
     * @param height
     * @param width
     */
    onResize = ({ height, width }) => {
        console.log('onResize h=' + height + ' w=' + width);
        this.setState(state => {
            return {
                timeScale: TimeScale.forNewSource(
                    state.timeScale,
                    new Range(VERTICAL_AXIS_WIDTH, width - VERTICAL_AXIS_WIDTH),
                ),
                levelScale: LevelScale.forNewSource(
                    state.levelScale,
                    new Range(height - HORZ_AXIS_HEIGHT, HORZ_AXIS_HEIGHT),
                ),
            };
        });
    };

    handleClickPoint = (event: React.MouseEvent) => {
        if (event.target && event.target instanceof SVGCircleElement) {
            const circle: SVGCircleElement = event.target;
            const point = this.flightPoints[circle.id];
            if (point?.point) {
                this.props.pointSelectionHandler(point.point);
            }
        }
    };

    static getAirspaceCrossingForAirspaceId(
        crossings: Array<FlightAirspaceCrossing>,
        id: string,
        entry: Date,
        exit: Date,
    ): FlightAirspaceCrossing {
        return crossings?.find(
            fac =>
                Utils.isEquals(fac.airspaceId, id) &&
                TimeUtils.isEquals(fac.firstEntryTime, entry) &&
                TimeUtils.isEquals(fac.lastExitTime, exit),
        );
    }

    //@InnoveSpecific
    static getDynamicVolumeCrossingForDynamicVolumeId(
        crossings: Array<FlightDynamicVolumeCrossing>,
        id: string,
        entry: Date,
        exit: Date,
    ): FlightDynamicVolumeCrossing {
        return crossings?.find(
            fac =>
                Utils.isEquals(fac.dynamicVolumeId, id) &&
                TimeUtils.isEquals(fac.firstEntryTime, entry) &&
                TimeUtils.isEquals(fac.lastExitTime, exit),
        );
    }

    static getAirspaceCrossingsForAirspaceId(
        crossings: Array<FlightAirspaceCrossing>,
        id: string,
    ): Array<FlightAirspaceCrossing> {
        return crossings?.filter(fac => Utils.isEquals(fac.airspaceId, id));
    }

    //@InnoveSpecific
    static getDynamicVolumeCrossingsForDynamicVolumeId(
        crossings: Array<FlightDynamicVolumeCrossing>,
        id: string,
    ): Array<FlightDynamicVolumeCrossing> {
        return crossings?.filter(fac => Utils.isEquals(fac.dynamicVolumeId, id));
    }

    private static getSelectedAirspaceCrossings(
        flight: FlightDto,
        modelType: FlightModelType,
        airspaceColourHighlights: Array<AirspaceColourHighlight>,
    ): Array<FlightAirspaceCrossing> {
        if (Utils.isNotExists(airspaceColourHighlights)) return undefined;

        const facs: Array<FlightAirspaceCrossing> = new Array<FlightAirspaceCrossing>();
        for (const airspaceColourHighlight of airspaceColourHighlights) {
            //if has times look for the specific airspace, otherwise return all airspaces with the given id independently of the time period
            if (Utils.isExists(airspaceColourHighlight.entryTime) && Utils.isExists(airspaceColourHighlight.exitTime)) {
                const fac: FlightAirspaceCrossing = VerticalProfileSvg.getAirspaceCrossingForAirspaceId(
                    FlightUtils.getAirspacePoints(flight, modelType),
                    airspaceColourHighlight.airspace.id,
                    airspaceColourHighlight.entryTime,
                    airspaceColourHighlight.exitTime,
                );
                if (Utils.isExists(fac)) {
                    facs.push(fac);
                } else {
                    // MJACQUES: commented out dynamic volume related code
                    //AirspaceColourHighlight is reused to highlight Dynamic Volume
                    // let fdvc: FlightDynamicVolumeCrossing = VerticalProfileSvg.getDynamicVolumeCrossingsForDynamicVolumeId(FlightUtils.getDynamicVolumes(flight, modelType), airspaceColourHighlight.airspace.id, airspaceColourHighlight.entryTime, airspaceColourHighlight.exitTime);
                    // if (Utils.isExists(fdvc)) {
                    //     facs.push(VerticalProfileSvg.getFlightAirspaceCrossingFromDynamicVolumeCrossing(fdvc));
                    // }
                }
            } else {
                const airspaceCrossingsForAirspaceId: Array<FlightAirspaceCrossing> =
                    VerticalProfileSvg.getAirspaceCrossingsForAirspaceId(
                        FlightUtils.getAirspacePoints(flight, modelType),
                        airspaceColourHighlight.airspace.id,
                    );
                if (Utils.isNotEmpty(airspaceCrossingsForAirspaceId)) {
                    for (const fac of airspaceCrossingsForAirspaceId) {
                        facs.push(fac);
                    }
                }
                const dynamicVolumeCrossingsForDynamicVolumeId: Array<FlightDynamicVolumeCrossing> =
                    VerticalProfileSvg.getDynamicVolumeCrossingsForDynamicVolumeId(
                        FlightUtils.getAirspacePoints(flight, modelType),
                        airspaceColourHighlight.airspace.id,
                    );
                if (Utils.isNotEmpty(dynamicVolumeCrossingsForDynamicVolumeId)) {
                    for (const fdvc of dynamicVolumeCrossingsForDynamicVolumeId) {
                        facs.push(VerticalProfileSvg.getFlightAirspaceCrossingFromDynamicVolumeCrossing(fdvc));
                    }
                }
            }
        }
        return facs;
    }

    /**
     * To reuse all the airspace logic to avoid big changes we need to convert a FlightDynamicVolumeCrossing into a  FlightAirspaceCrossing.
     * @param fdvc
     * @return
     */
    //@InnoveSpecific
    private static getFlightAirspaceCrossingFromDynamicVolumeCrossing(
        fdvc: FlightDynamicVolumeCrossing,
    ): FlightAirspaceCrossing {
        return {
            airspaceId: fdvc.dynamicVolumeId,
            firstEntryTime: fdvc.firstEntryTime,
            lastExitTime: fdvc.lastExitTime,
            occupanyInMs: fdvc.occupanyInMs,
            occupancyDistance: fdvc.occupancyDistance,
            firstEntryDistance: fdvc.firstEntryDistance,
            firstEntryTrend: fdvc.firstEntryTrend,
            middleTrend: fdvc.middleTrend,
            lastExitTrend: fdvc.lastExitTrend,
            firstEntryFlightLevel: fdvc.firstEntryFlightLevel,
            lastExitFlightLevel: fdvc.lastExitFlightLevel,
            activated: fdvc.activated,
        };
    }
}
